import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { Plus, Trash2, Power, ChevronRight, Sparkles, Calendar, Tag, Clock } from "lucide-react";

const DAYS = [
    { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
    { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" }, { v: 6, label: "Sun" },
];

const CATEGORIES = [
    "Food & Beverage", "Grocery", "Apparel", "Pharmacy", "Electronics",
    "Fuel", "Hotel", "Travel", "Salon", "Home & Furniture", "Online Shopping", "Other",
];

const PRESETS = [
    { name: "Weekend 2x on Food", category: "Food & Beverage", days_of_week: [5, 6], multiplier: 2.0 },
    { name: "Happy Hour 3x (4pm – 7pm)", days_of_week: null, hour_from: 16, hour_to: 19, multiplier: 3.0 },
    { name: "Welcome — 200 bonus on first bill", first_bill_only: true, bonus_points: 200 },
    { name: "Big-spender — +500 over ₹5,000", min_value: 5000, bonus_points: 500 },
    { name: "Grocery 1.5x", category: "Grocery", multiplier: 1.5 },
];

function emptyRule() {
    return {
        name: "",
        description: "",
        active: true,
        priority: 100,
        condition: {
            min_value: null, max_value: null,
            merchant_categories: [], merchant_name_contains: [],
            days_of_week: [], hour_from: null, hour_to: null,
            first_bill_only: false, new_customer_within_days: null,
        },
        action: { bonus_points: null, multiplier: null, max_bonus_points: null },
    };
}

export default function AdminLoyaltyRules() {
    const [rules, setRules] = useState([]);
    const [form, setForm] = useState(emptyRule());
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [testCtx, setTestCtx] = useState({ total: 2909, merchant_category: "Food & Beverage" });

    const load = async () => {
        const r = await adminApi.get("/api/admin/loyalty-rules");
        setRules(r.data.rules);
    };
    useEffect(() => { load(); }, []);

    const toggleDay = (v) => {
        setForm((f) => {
            const cur = f.condition.days_of_week || [];
            const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v].sort();
            return { ...f, condition: { ...f.condition, days_of_week: next } };
        });
    };
    const toggleCat = (c) => {
        setForm((f) => {
            const cur = f.condition.merchant_categories || [];
            const next = cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c];
            return { ...f, condition: { ...f.condition, merchant_categories: next } };
        });
    };

    const save = async () => {
        if (!form.name) return;
        setBusy(true);
        try {
            if (editing) {
                await adminApi.patch(`/api/admin/loyalty-rules/${editing}`, form);
            } else {
                await adminApi.post("/api/admin/loyalty-rules", form);
            }
            setForm(emptyRule());
            setEditing(null);
            await load();
        } finally { setBusy(false); }
    };

    const edit = (r) => {
        setEditing(r.id);
        setForm({
            name: r.name, description: r.description || "", active: !!r.active, priority: r.priority ?? 100,
            condition: { ...emptyRule().condition, ...(r.condition || {}) },
            action: { ...emptyRule().action, ...(r.action || {}) },
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (id) => {
        if (!confirm("Delete this rule?")) return;
        await adminApi.delete(`/api/admin/loyalty-rules/${id}`);
        await load();
    };

    const toggleActive = async (r) => {
        await adminApi.patch(`/api/admin/loyalty-rules/${r.id}`, { active: !r.active });
        await load();
    };

    const applyPreset = (p) => {
        const f = emptyRule();
        f.name = p.name;
        if (p.category) f.condition.merchant_categories = [p.category];
        if (p.days_of_week) f.condition.days_of_week = p.days_of_week;
        if (p.hour_from !== undefined) { f.condition.hour_from = p.hour_from; f.condition.hour_to = p.hour_to; }
        if (p.first_bill_only) f.condition.first_bill_only = true;
        if (p.min_value) f.condition.min_value = p.min_value;
        if (p.multiplier) f.action.multiplier = p.multiplier;
        if (p.bonus_points) f.action.bonus_points = p.bonus_points;
        setForm(f);
    };

    const runTest = async () => {
        const r = await adminApi.post("/api/admin/loyalty-rules/test", testCtx);
        setTestResult(r.data);
    };

    return (
        <div>
            <h1 className="font-display text-3xl font-bold flex items-center gap-2">
                <Sparkles className="text-[#c9a959]" /> Loyalty Rules
            </h1>
            <p className="text-sm text-[#6b7280]">Configure bonus points & multipliers on top of the base 0.1 pt / ₹ earn rate.</p>

            {/* Editor */}
            <section className="mt-6 fx-card p-5">
                <h2 className="font-display text-lg font-semibold mb-4">{editing ? "Edit rule" : "Create new rule"}</h2>

                <div className="mb-4 flex flex-wrap gap-2">
                    {PRESETS.map((p) => (
                        <button key={p.name} onClick={() => applyPreset(p)} className="fx-chip bg-[#c9a959]/10 text-[#0b1437] border border-[#c9a959]/30 hover:bg-[#c9a959]/20">
                            + {p.name}
                        </button>
                    ))}
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Rule name">
                        <input data-testid="rule-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fx-input" placeholder="e.g. Weekend 2x on Food" />
                    </Field>
                    <Field label="Priority (lower = first)">
                        <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 100 })} className="fx-input" />
                    </Field>
                    <Field label="Description" wide>
                        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="fx-input" placeholder="Internal note" />
                    </Field>
                </div>

                <h3 className="font-semibold text-sm mt-5 mb-2 flex items-center gap-1"><Tag size={14} /> Condition</h3>
                <div className="grid md:grid-cols-4 gap-3">
                    <Field label="Min bill ₹">
                        <input type="number" value={form.condition.min_value ?? ""} onChange={(e) => setForm({ ...form, condition: { ...form.condition, min_value: e.target.value ? parseFloat(e.target.value) : null } })} className="fx-input" />
                    </Field>
                    <Field label="Max bill ₹">
                        <input type="number" value={form.condition.max_value ?? ""} onChange={(e) => setForm({ ...form, condition: { ...form.condition, max_value: e.target.value ? parseFloat(e.target.value) : null } })} className="fx-input" />
                    </Field>
                    <Field label="Hour from (24h)">
                        <input type="number" min={0} max={23} value={form.condition.hour_from ?? ""} onChange={(e) => setForm({ ...form, condition: { ...form.condition, hour_from: e.target.value === "" ? null : parseInt(e.target.value) } })} className="fx-input" />
                    </Field>
                    <Field label="Hour to (24h)">
                        <input type="number" min={0} max={23} value={form.condition.hour_to ?? ""} onChange={(e) => setForm({ ...form, condition: { ...form.condition, hour_to: e.target.value === "" ? null : parseInt(e.target.value) } })} className="fx-input" />
                    </Field>
                </div>

                <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-widest text-[#6b7280] flex items-center gap-1"><Calendar size={11} /> Days of week (empty = any)</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                        {DAYS.map((d) => {
                            const on = form.condition.days_of_week?.includes(d.v);
                            return (
                                <button key={d.v} onClick={() => toggleDay(d.v)} className={`px-3 py-1 rounded-full text-xs font-semibold ${on ? "bg-[#0b1437] text-white" : "bg-[#f3f4f6] text-[#4b5563] border border-[rgba(11,20,55,0.08)]"}`}>{d.label}</button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-widest text-[#6b7280]">Merchant categories (empty = any)</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                        {CATEGORIES.map((c) => {
                            const on = form.condition.merchant_categories?.includes(c);
                            return (
                                <button key={c} onClick={() => toggleCat(c)} className={`px-3 py-1 rounded-full text-xs ${on ? "bg-[#c9a959] text-black font-semibold" : "bg-[#f3f4f6] text-[#4b5563] border border-[rgba(11,20,55,0.08)]"}`}>{c}</button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-3 mt-3">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.condition.first_bill_only} onChange={(e) => setForm({ ...form, condition: { ...form.condition, first_bill_only: e.target.checked } })} /> First bill only</label>
                    <Field label="New customer within (days)">
                        <input type="number" value={form.condition.new_customer_within_days ?? ""} onChange={(e) => setForm({ ...form, condition: { ...form.condition, new_customer_within_days: e.target.value ? parseInt(e.target.value) : null } })} className="fx-input" />
                    </Field>
                </div>

                <h3 className="font-semibold text-sm mt-5 mb-2 flex items-center gap-1"><Sparkles size={14} /> Reward action</h3>
                <div className="grid md:grid-cols-3 gap-3">
                    <Field label="Multiplier (e.g. 2.0 = 2x)">
                        <input type="number" step="0.1" value={form.action.multiplier ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, multiplier: e.target.value ? parseFloat(e.target.value) : null } })} className="fx-input" />
                    </Field>
                    <Field label="Flat bonus points">
                        <input type="number" value={form.action.bonus_points ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, bonus_points: e.target.value ? parseInt(e.target.value) : null } })} className="fx-input" />
                    </Field>
                    <Field label="Cap bonus at (pts)">
                        <input type="number" value={form.action.max_bonus_points ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, max_bonus_points: e.target.value ? parseInt(e.target.value) : null } })} className="fx-input" />
                    </Field>
                </div>

                <div className="flex items-center gap-3 mt-4">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
                    <button data-testid="save-rule-btn" onClick={save} disabled={busy || !form.name} className="fx-btn-primary ml-auto">
                        {editing ? "Save changes" : "Create rule"}
                    </button>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyRule()); }} className="fx-btn-ghost">Cancel</button>}
                </div>
            </section>

            {/* Tester */}
            <section className="mt-6 fx-card p-5">
                <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Clock size={16} /> Test a bill</h2>
                <div className="grid md:grid-cols-4 gap-3 items-end">
                    <Field label="Bill total ₹">
                        <input type="number" value={testCtx.total} onChange={(e) => setTestCtx({ ...testCtx, total: parseFloat(e.target.value) || 0 })} className="fx-input" />
                    </Field>
                    <Field label="Merchant category">
                        <select value={testCtx.merchant_category || ""} onChange={(e) => setTestCtx({ ...testCtx, merchant_category: e.target.value })} className="fx-input">
                            <option value="">— any —</option>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="When (IST, optional)">
                        <input type="datetime-local" onChange={(e) => setTestCtx({ ...testCtx, when_ist: e.target.value ? e.target.value + ":00+05:30" : null })} className="fx-input" />
                    </Field>
                    <button onClick={runTest} className="fx-btn-primary">Test rules</button>
                </div>
                {testResult && (
                    <div className="mt-3 p-3 rounded-lg bg-[#f9fafb] text-sm">
                        <p>Base: <b>{testResult.base_points}</b> pts · Bonus: <b className="text-[#c9a959]">+{testResult.bonus_points}</b> · Total: <b>{testResult.total_points}</b> pts (×{testResult.multiplier_total})</p>
                        {testResult.applied?.length > 0 && (
                            <ul className="mt-2 text-xs text-[#4b5563] list-disc list-inside">
                                {testResult.applied.map((a) => <li key={a.id}><b>{a.name}</b> → +{a.bonus} pts</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </section>

            {/* List */}
            <section className="mt-6">
                <h2 className="font-display text-lg font-semibold mb-3">Configured rules ({rules.length})</h2>
                <div className="space-y-2">
                    {rules.length === 0 && <p className="text-sm text-[#6b7280]">No rules yet. Pick a preset above or define your own.</p>}
                    {rules.map((r) => (
                        <div key={r.id} className="fx-card p-4 flex items-center gap-4">
                            <div className={`w-2 h-12 rounded-full ${r.active ? "bg-[#16a34a]" : "bg-[#9ca3af]"}`} />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold">{r.name}</p>
                                <p className="text-xs text-[#6b7280] truncate">{r.description || summarise(r)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-[#6b7280]">Action</p>
                                <p className="font-semibold text-sm">
                                    {r.action?.multiplier ? `×${r.action.multiplier} ` : ""}
                                    {r.action?.bonus_points ? `+${r.action.bonus_points} pts` : ""}
                                </p>
                            </div>
                            <button onClick={() => toggleActive(r)} title={r.active ? "Disable" : "Enable"} className="fx-btn-ghost !py-1.5">
                                <Power size={14} className={r.active ? "text-[#16a34a]" : "text-[#9ca3af]"} />
                            </button>
                            <button onClick={() => edit(r)} className="fx-btn-ghost !py-1.5">
                                <ChevronRight size={14} />
                            </button>
                            <button onClick={() => remove(r.id)} className="fx-btn-ghost !py-1.5 !text-[#dc2626]">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

function summarise(r) {
    const c = r.condition || {};
    const a = r.action || {};
    const parts = [];
    if (c.merchant_categories?.length) parts.push(`cat: ${c.merchant_categories.join(", ")}`);
    if (c.days_of_week?.length) parts.push(`days: ${c.days_of_week.map((d) => DAYS[d]?.label).join("/")}`);
    if (c.hour_from != null) parts.push(`${c.hour_from}h–${c.hour_to}h`);
    if (c.min_value) parts.push(`min ₹${c.min_value}`);
    if (c.first_bill_only) parts.push("first bill");
    if (a.multiplier) parts.push(`×${a.multiplier}`);
    if (a.bonus_points) parts.push(`+${a.bonus_points} pts`);
    return parts.join(" · ");
}

function Field({ label, children, wide }) {
    return (
        <div className={wide ? "md:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-widest text-[#6b7280]">{label}</label>
            {children}
        </div>
    );
}
